import { useEffect, useState, useContext, ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box } from '@mantine/core';
import { useIntersection } from '@mantine/hooks';
import dayjs from 'dayjs';
import { CallStatus, ICall } from 'interfaces/Reporting';
import { useDeviceType } from 'hooks/useDeviceType';
import CallHistoryTableDesktopView from './DesktopView';
import CallHistoryTableTabletView from './TabletView';
import AuthContext from 'context/AuthContext';
import { AccessLevel } from 'interfaces/GetUserResult';
import ReportContext from 'context/ReportContext';
import { useNotification } from 'context/NotificationContext';
import { downloadAsCSV } from 'utils/file';
import { formatPhoneNumber } from 'utils/phone';
import { logger, ErrorType } from '../../../../utils/logger';
import { formatSeconds } from 'utils/date';
import {
  callStatusLabels,
  getHighestSentiment,
  getValidSentiments,
  sentimentLabels,
} from 'pages/reporting/utils';
import { convertDecimalToPercentage } from 'utils/number';
import { callStatusColors } from 'constants/reporting';
import { DateRange } from 'components/DateRangeFilter/Dropdown';
import useCallsQuery from 'hooks/useCallsQuery';
import { generateMockCallsForFreeAccounts } from 'utils/mockData';
import SentimentSmiley from './SetimentSmiley';

export interface ICallHistoryTableData {
  id: string;
  phoneNumber: string;
  startTime: string;
  duration: string;
  status: string;
  sentiment: string;
  isIncomplete: boolean;
  statusColor: string;
  sentimentSmiley: ReactNode;
  sentimentPercentage?: string;
}

export const CallHistoryTable = () => {
  const {
    data: { account },
  } = useContext(AuthContext);
  const {
    data: { dateRange },
  } = useContext(ReportContext);
  const navigate = useNavigate();
  const { isTablet } = useDeviceType();
  const { showError } = useNotification();
  const [searchParams] = useSearchParams();

  const [isDownloading, setIsDownloading] = useState(false);
  const [chartDateRange, setChartDateRange] = useState<DateRange | undefined>(
    dateRange
  );
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useCallsQuery({ dateRange });

  const { ref, entry } = useIntersection({
    root: null,
    threshold: 1,
  });

  const isPremium = account?.access_level === AccessLevel.PRO;
  const allCalls = data?.pages.flatMap((page) => page.items) || [];

  const handleViewDetails = (id: string) => {
    navigate(`/reporting/${id}`, {
      state: { dateSearch: searchParams.toString() },
    });
  };

  const handleExport = () => {
    setIsDownloading(true);
    try {
      const csvData = formattedData.map((call) => ({
        'Phone Number': call.phoneNumber,
        'Start Time': call.startTime,
        Duration: call.duration,
        Status: call.status,
        Sentiment: call.sentiment,
        'Sentiment Percentage': call.sentimentPercentage || 'N/A',
      }));

      const timestamp = new Date().toISOString().split('T')[0];
      downloadAsCSV(csvData, `call_history_${timestamp}.csv`);
    } catch (error) {
          logger.error('Failed to export call history', error as Error, {
            errorType: ErrorType.API_ERROR,
            component: 'CallHistoryTable'
          });
      showError('Failed to export call history. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    if (
      dateRange?.from !== chartDateRange?.from ||
      dateRange?.to !== chartDateRange?.to
    ) {
      setChartDateRange(dateRange);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  useEffect(() => {
    if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry?.isIntersecting, hasNextPage, isFetchingNextPage]);

  const getFormattedData = (data: ICall[]) => {
    return data.map((call) => {
      const validSentiments = getValidSentiments(call.sentiment);
      const [sentiment, sentimentValue] = getHighestSentiment(validSentiments);
      const isSentimentEmpty = sentimentValue === 0;

      return {
        id: call.id,
        phoneNumber: formatPhoneNumber(call.participant.phone_number),
        startTime: dayjs(call.start_time).format('MMM D, YYYY HH:mm:ss'),
        duration: formatSeconds(call.duration),
        status: callStatusLabels[call.resolution as CallStatus],
        isIncomplete: call.resolution === CallStatus.INCOMPLETE,
        sentiment: isSentimentEmpty ? 'N/A' : sentimentLabels[sentiment],
        statusColor: callStatusColors[call.resolution as CallStatus],
        sentimentSmiley: (
          <SentimentSmiley callSentiment={call.sentiment} size={19} />
        ),
        sentimentPercentage: isSentimentEmpty
          ? undefined
          : `${convertDecimalToPercentage(sentimentValue)}%`,
      };
    });
  };

  const renderMockContent = () => (
    <>
      {isTablet ? (
        <CallHistoryTableTabletView
          data={getFormattedData(generateMockCallsForFreeAccounts(0, 1))}
          onViewDetails={() => {}}
          onExport={() => {}}
        />
      ) : (
        <CallHistoryTableDesktopView
          data={getFormattedData(generateMockCallsForFreeAccounts(0, 8))}
          onViewDetails={() => {}}
          onExport={() => {}}
        />
      )}
    </>
  );

  if (!isPremium) {
    return renderMockContent();
  }

  const formattedData = getFormattedData(allCalls);

  return (
    <>
      {isTablet ? (
        <CallHistoryTableTabletView
          data={formattedData}
          onViewDetails={handleViewDetails}
          onExport={handleExport}
          isLoading={isLoading}
          isDownloading={isDownloading}
        />
      ) : (
        <CallHistoryTableDesktopView
          data={formattedData}
          onViewDetails={handleViewDetails}
          onExport={handleExport}
          isLoading={isLoading}
          isDownloading={isDownloading}
        />
      )}

      {/* Intersection observer target */}
      <Box ref={ref} />
    </>
  );
};

export default CallHistoryTable;
